import { useState } from 'react';
import {
  Brain, Image as ImageIcon, Mic, Bot, Wrench, Eye, Cloud,
  Database, MessageSquare, CreditCard, Briefcase, ShieldCheck,
} from 'lucide-react';
import './PublicSite.css';
import './Capabilities.css';

const CATEGORIES = [
  {
    key: 'ai-models',
    icon: Brain,
    title: 'AI Models & APIs',
    blurb: 'Best-in-class language models for chat, agents, reasoning, and analysis.',
    items: [
      { name: 'OpenAI', detail: 'GPT-5, GPT-4o, o-series reasoning models, embeddings' },
      { name: 'Anthropic', detail: 'Claude Sonnet 4.5, Opus 4.5, Haiku 4.5' },
      { name: 'Google', detail: 'Gemini 3 Pro, Gemini 3 Flash' },
      { name: 'xAI', detail: 'Grok' },
      { name: 'Mistral', detail: 'Mistral Large, Codestral' },
      { name: 'Meta', detail: 'Llama 3 (open-weight, on-prem deployable)' },
      { name: 'Cohere', detail: 'Command R+, embeddings, rerankers' },
      { name: 'DeepSeek', detail: 'Cost-efficient reasoning models' },
    ],
  },
  {
    key: 'image-video',
    icon: ImageIcon,
    title: 'Image & Video Generation',
    blurb: 'Photorealistic, illustrative, and cinematic media on demand.',
    items: [
      { name: 'OpenAI GPT Image 1', detail: 'High-quality photoreal & graphic generation' },
      { name: 'Gemini Nano Banana', detail: 'Latest Google image generation' },
      { name: 'Sora 2', detail: 'OpenAI text-to-video' },
      { name: 'Runway Gen-3', detail: 'Cinematic video, motion brush' },
      { name: 'Stable Diffusion / SDXL', detail: 'Open-weight, customizable, on-prem deployable' },
      { name: 'fal.ai', detail: 'Fast hosted inference for SD, Flux, custom models' },
      { name: 'Midjourney', detail: 'Stylized art generation (via API partners)' },
    ],
  },
  {
    key: 'voice-audio',
    icon: Mic,
    title: 'Voice & Audio',
    blurb: 'Natural voice synthesis, transcription, and real-time conversation.',
    items: [
      { name: 'ElevenLabs', detail: 'Hyper-realistic TTS, voice cloning, multilingual' },
      { name: 'OpenAI TTS & Whisper', detail: 'Speech synthesis + transcription' },
      { name: 'Deepgram', detail: 'Real-time streaming ASR' },
      { name: 'AssemblyAI', detail: 'Transcription with speaker diarization' },
      { name: 'Twilio Voice', detail: 'Programmable phone calls, IVR, conference' },
    ],
  },
  {
    key: 'robotics-humanoid',
    icon: Bot,
    title: 'Humanoid Robotics',
    blurb: 'Two-legged service, research, and demonstration robots.',
    items: [
      { name: 'Unitree H1', detail: 'Full-size humanoid, $90K+ class' },
      { name: 'Unitree G1', detail: 'Compact humanoid, accessible R&D price point' },
      { name: 'Figure 01 / 02', detail: 'Industrial humanoid (partnership availability)' },
      { name: '1X NEO', detail: 'Home/office humanoid (waitlist)' },
      { name: 'Apptronik Apollo', detail: 'Logistics-focused humanoid' },
    ],
  },
  {
    key: 'robotics-mobile',
    icon: Bot,
    title: 'Quadruped & Mobile Robotics',
    blurb: 'Four-legged patrol/inspection bots and autonomous mobile robots.',
    items: [
      { name: 'Unitree Go2', detail: 'Compact quadruped for inspection & demos' },
      { name: 'Unitree B2', detail: 'Industrial quadruped, payload + outdoor IP rating' },
      { name: 'Boston Dynamics Spot', detail: 'Industrial inspection (partner channel)' },
      { name: 'ANYmal', detail: 'ANYbotics rugged quadruped' },
      { name: 'AMRs', detail: 'Locus, Geek+, MiR, and Otto autonomous warehouse robots' },
    ],
  },
  {
    key: 'industrial',
    icon: Wrench,
    title: 'Industrial Robotics & Cobots',
    blurb: 'Stationary arms and collaborative robots for manufacturing lines.',
    items: [
      { name: 'FANUC', detail: 'Industrial arms & CRX cobots' },
      { name: 'Universal Robots', detail: 'UR3/5/10/16 cobot family' },
      { name: 'KUKA', detail: 'Heavy-duty industrial robotics' },
      { name: 'ABB', detail: 'YuMi cobots, IRB industrial arms' },
      { name: 'Doosan / Techman', detail: 'Cobot alternatives at competitive pricing' },
    ],
  },
  {
    key: 'vision',
    icon: Eye,
    title: 'Computer Vision',
    blurb: 'Object detection, quality inspection, security, and OCR.',
    items: [
      { name: 'Roboflow', detail: 'Custom vision model training & deployment' },
      { name: 'AWS Rekognition', detail: 'Hosted vision: faces, labels, moderation' },
      { name: 'Google Vision AI', detail: 'OCR, product search, document AI' },
      { name: 'Azure AI Vision', detail: 'Vision APIs + custom models' },
      { name: 'YOLO / Ultralytics', detail: 'Open-source real-time detection (on-prem)' },
    ],
  },
  {
    key: 'cloud',
    icon: Cloud,
    title: 'Cloud, Compute & Hosting',
    blurb: 'Where your AI lives — managed inference, GPUs, and infrastructure.',
    items: [
      { name: 'AWS', detail: 'EC2, Bedrock, SageMaker, Lambda' },
      { name: 'Google Cloud', detail: 'Vertex AI, Cloud Run, BigQuery' },
      { name: 'Azure', detail: 'OpenAI Service, Functions, ML Studio' },
      { name: 'Replicate', detail: 'Hosted open-source model inference' },
      { name: 'RunPod / Modal', detail: 'GPU compute on demand' },
      { name: 'Vercel / Render / Railway', detail: 'App + API hosting' },
    ],
  },
  {
    key: 'data',
    icon: Database,
    title: 'Data & Knowledge',
    blurb: 'Where your information is stored and retrieved by AI.',
    items: [
      { name: 'MongoDB', detail: 'Document database, primary store for most apps' },
      { name: 'PostgreSQL', detail: 'Relational database with vector extension (pgvector)' },
      { name: 'Pinecone', detail: 'Managed vector database for RAG' },
      { name: 'Weaviate / Qdrant', detail: 'Open-source vector databases' },
      { name: 'Supabase', detail: 'Postgres + auth + storage in a box' },
      { name: 'Snowflake / BigQuery', detail: 'Enterprise data warehousing' },
    ],
  },
  {
    key: 'communications',
    icon: MessageSquare,
    title: 'Communications',
    blurb: 'Email, SMS, chat, and notification delivery.',
    items: [
      { name: 'Twilio', detail: 'SMS, voice, WhatsApp, email (via SendGrid)' },
      { name: 'Resend', detail: 'Modern transactional email API' },
      { name: 'SendGrid', detail: 'Enterprise email delivery' },
      { name: 'Slack', detail: 'Notifications, slash commands, workflow bots' },
      { name: 'Discord', detail: 'Community bots & integrations' },
      { name: 'Telegram', detail: 'Bot API for channels & chats' },
    ],
  },
  {
    key: 'payments',
    icon: CreditCard,
    title: 'Payments & Billing',
    blurb: 'Accept payments, subscriptions, and run usage-based billing.',
    items: [
      { name: 'Stripe', detail: 'Subscriptions, one-time, marketplace payouts, crypto' },
      { name: 'PayPal', detail: 'Consumer-friendly checkout' },
      { name: 'Square', detail: 'In-person + online for small businesses' },
      { name: 'Razorpay', detail: 'India-focused payments' },
    ],
  },
  {
    key: 'productivity',
    icon: Briefcase,
    title: 'Business Systems & Productivity',
    blurb: 'Connect AI to the tools your team already lives in.',
    items: [
      { name: 'Google Workspace', detail: 'Gmail, Calendar, Drive, Docs, Sheets' },
      { name: 'Microsoft 365', detail: 'Outlook, Teams, OneDrive, SharePoint' },
      { name: 'Salesforce / HubSpot', detail: 'CRM read/write & automation' },
      { name: 'Notion / Airtable', detail: 'Knowledge bases & structured data' },
      { name: 'Zapier / Make', detail: 'Workflow glue across 5,000+ apps' },
      { name: 'Custom ERPs', detail: 'NetSuite, SAP, Odoo, QuickBooks integration' },
    ],
  },
  {
    key: 'auth',
    icon: ShieldCheck,
    title: 'Authentication & Security',
    blurb: 'Identity, access control, and compliance.',
    items: [
      { name: 'Auth0 / Clerk', detail: 'Drop-in user authentication' },
      { name: 'Google OAuth', detail: 'Social sign-in via Google account' },
      { name: 'Firebase Auth', detail: 'Phone, email, social auth managed' },
      { name: 'Okta', detail: 'Enterprise SSO & SAML' },
      { name: 'JWT-based custom', detail: 'When you need full control' },
    ],
  },
];

export default function Capabilities() {
  const [activeKey, setActiveKey] = useState(null);

  return (
    <div className="page capabilities-page">
      {/* Hero */}
      <section className="page-hero capabilities-hero">
        <div className="container">
          <p className="capabilities-eyebrow">Integration Capabilities</p>
          <h1>If it connects to an API, we can integrate it.</h1>
          <p className="lead">
            We're vendor-agnostic. Here's a snapshot of the platforms, models, and hardware we
            actively work with — plus an open invitation to discuss anything that isn't on this list.
          </p>
          <div className="capabilities-actions">
            <a href="/ai-services#inquiry" className="btn btn-primary" data-testid="capabilities-cta-inquiry">
              Start an inquiry →
            </a>
            <a href="/ai-services" className="btn btn-ghost-light" data-testid="capabilities-cta-services">
              ← Back to Services
            </a>
          </div>
        </div>
      </section>

      {/* Category jumper */}
      <section className="capabilities-jumper">
        <div className="container">
          <div className="jumper-pills">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.key}
                  href={`#${c.key}`}
                  className="jumper-pill"
                  data-testid={`jumper-${c.key}`}
                >
                  <Icon size={14} />
                  <span>{c.title}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories */}
      {CATEGORIES.map((cat) => {
        const Icon = cat.icon;
        return (
          <section
            key={cat.key}
            id={cat.key}
            className="content-section capabilities-section"
            data-testid={`capability-section-${cat.key}`}
          >
            <div className="container">
              <header className="capability-header">
                <div className="capability-icon">
                  <Icon size={26} />
                </div>
                <div>
                  <h2>{cat.title}</h2>
                  <p>{cat.blurb}</p>
                </div>
              </header>
              <div className="capability-grid">
                {cat.items.map((item) => (
                  <button
                    key={item.name}
                    type="button"
                    className={`capability-tile ${activeKey === `${cat.key}:${item.name}` ? 'active' : ''}`}
                    onClick={() => setActiveKey(activeKey === `${cat.key}:${item.name}` ? null : `${cat.key}:${item.name}`)}
                    data-testid={`capability-tile-${cat.key}-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                  >
                    <span className="tile-name">{item.name}</span>
                    <span className="tile-detail">{item.detail}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Don't see it? */}
      <section className="content-section capabilities-cta">
        <div className="container">
          <h2>Don't see what you need?</h2>
          <p>
            This list keeps growing. If your stack uses something not shown here — a niche industry
            platform, a custom internal tool, or a brand-new model that just dropped — we can almost
            certainly integrate it. Tell us what you're working with.
          </p>
          <a href="/ai-services#inquiry" className="btn btn-primary" data-testid="capabilities-bottom-cta">
            Start a conversation →
          </a>
        </div>
      </section>
    </div>
  );
}
